/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useState, useContext, useEffect, useRef } from 'react';
import { Tooltip } from 'antd';
import { useThemeSwitcher } from 'react-css-theme-switcher';

import doReturnTheme from '~src/styles/themeEngine';
import IconButton from '~components/_common/IconButton/IconButton';
import translate from '~src/i18n/translate';
import AppContext from '~src/contexts/AppContext';
import { TimeMachineModal } from '~components/popups-modals/TimeMachineModal/TimeMachineModal';
import { useHistory } from 'react-router-dom';
import { useProjectId } from '~src/hooks/useProjectId';
import { removeStorageTimestamp } from '~src/utils/sessionstorageQueries';

type TimeMachineProps = {
  publicPage?: boolean;
};

const TimeMachine: React.FC<TimeMachineProps> = ({ publicPage }) => {
  const { switcher, currentTheme, themes } = useThemeSwitcher();
  const theme = doReturnTheme(currentTheme);

  const [modalState, setModalState] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // get the App Context (normal, time-machine, super-user)
  const { appContextState, setAppContextState } = useContext(AppContext);
  const history = useHistory();
  const projectId = useProjectId();
  const buttonRef = useRef<HTMLDivElement>(null);

  const setVisible = (state) => {
    setModalState(state);
  };

  useEffect(() => {
    const hoverEnterHandler = () => {
      setShowTooltip(true);
    };
    const hoverLeftHandler = () => {
      setShowTooltip(false);
    };

    buttonRef.current?.addEventListener('mouseenter', hoverEnterHandler);
    buttonRef.current?.addEventListener('mouseleave', hoverLeftHandler);

    return () => {
      buttonRef.current?.removeEventListener('mouseenter', hoverEnterHandler);
      buttonRef.current?.removeEventListener('mouseleave', hoverLeftHandler);
    };
  }, [buttonRef.current]);

  const toggleTheme = () => {
    switch (window.sessionStorage.getItem('appState')) {
      case 'timeMachine':
        switcher({ theme: themes.light });
        setVisible(false);
        setAppContextState('normal');
        window.sessionStorage.setItem('appState', 'normal');
        window.sessionStorage.removeItem('timeMachineDate');
        !publicPage && history.push(`/projects/${projectId}`);
        break;
      default:
        switcher({ theme: themes.dark });
        setVisible(false);
        window.sessionStorage.setItem('appState', 'timeMachine');
        setAppContextState('time-machine');
        !publicPage && history.push(`/projects/${projectId}`);
        break;
    }
  };

  const style = {
    container: {
      display: 'flex',
      justifyContent: 'center',
      margin: '0px 5px',
      // width: theme.aboveWidth[2],
    },
    buttonDiv: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '40px',
      height: '40px',
      borderRadius: '20px',
      color: theme.colors.white,
      backgroundColor:
        appContextState === 'time-machine'
          ? theme.colors.darkGray
          : theme.colors.secondary,
      cursor: 'pointer',
      transition: '0.3s',

      ':hover': {
        backgroundColor:
          appContextState === 'time-machine'
            ? theme.colors.lightGray
            : theme.colors.highlightedSecondary,
      },
    },
    icons: {
      color: theme.colors.white,
      fontSize: '20px',
      transition: '0.3s',
    },
  };

  const offTimeMachine = () => {
    toggleTheme();
    removeStorageTimestamp();
  };

  return (
    <div css={style.container}>
      <Tooltip
        placement="bottom"
        title={
          window.sessionStorage.getItem('appState') === 'timeMachine'
            ? translate('exit-time-machine')
            : translate('time-machine')
        }
        visible={showTooltip}>
        <div
          css={style.buttonDiv}
          onClick={() =>
            window.sessionStorage.getItem('appState') === 'timeMachine'
              ? offTimeMachine()
              : setVisible(true)
          }
          role="button"
          tabIndex={0}
          ref={buttonRef}>
          <IconButton
            iconStyle="far"
            icon="history"
            customStyle={style.icons}
            backgroundOff
            // click={() => clicked()}
          />
        </div>
      </Tooltip>

      <TimeMachineModal
        modalState={modalState}
        onCancel={() => setVisible(false)}
        onOk={() => toggleTheme()}
      />
    </div>
  );
};

export default TimeMachine;
